(() => {
  const desktopKey = 'pluggameCollectionGridDesktop';
  const mobileKey = 'pluggameCollectionGridMobile';
  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const viewClasses = [
    'collection-grid-view--desktop-4',
    'collection-grid-view--desktop-6',
    'collection-grid-view--mobile-2',
    'collection-grid-view--mobile-3',
  ];

  const storage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        return;
      }
    },
  };

  const isDesktop = () => desktopQuery.matches;

  const getStoredView = () => {
    if (isDesktop()) {
      return storage.get(desktopKey) === '6' ? '6' : '4';
    }

    return storage.get(mobileKey) === '3' ? '3' : '2';
  };

  const setStoredView = (view) => {
    if (view === '4' || view === '6') {
      storage.set(desktopKey, view);
    }

    if (view === '2' || view === '3') {
      storage.set(mobileKey, view);
    }
  };

  const normalizeView = (view) => {
    if (isDesktop()) {
      return view === '6' ? '6' : '4';
    }

    return view === '3' ? '3' : '2';
  };

  const parsePositiveInteger = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const getActiveColumnCount = (root, viewport, view) => {
    if (viewport === 'desktop') {
      return view === '6' ? 6 : parsePositiveInteger(root.dataset.defaultDesktopColumns, 4);
    }

    return view === '3' ? 3 : parsePositiveInteger(root.dataset.defaultMobileColumns, 2);
  };

  const mapEditorialColumn = (desktopColumn, activeColumns) => {
    const column = clamp(parsePositiveInteger(desktopColumn, 1), 1, 4);

    if (activeColumns <= 1) {
      return 1;
    }

    return clamp(Math.round(((column - 1) / 3) * (activeColumns - 1)) + 1, 1, activeColumns);
  };

  const isTileInPosition = (grid, tile, referenceNode) => {
    if (referenceNode) {
      return tile.nextElementSibling === referenceNode;
    }

    return grid.lastElementChild === tile;
  };

  const layoutEditorialTiles = (root, activeColumns) => {
    const grid = root.querySelector('#product-grid');

    if (!grid) {
      return;
    }

    const editorialTiles = [];
    const seenEditorialTileIndexes = new Set();

    grid.querySelectorAll('[data-collection-editorial-tile]').forEach((tile) => {
      const tileIndex = tile.dataset.editorialTileIndex || `${tile.dataset.editorialTileRow}-${tile.dataset.editorialTileColumn}`;

      if (seenEditorialTileIndexes.has(tileIndex)) {
        tile.remove();
        return;
      }

      seenEditorialTileIndexes.add(tileIndex);
      editorialTiles.push(tile);
    });

    if (!editorialTiles.length) {
      delete root.dataset.editorialTilesReady;
      return;
    }

    const productItems = Array.from(grid.querySelectorAll('[data-collection-product-grid-item]'));
    const visibleTiles = [];

    const tileData = editorialTiles
      .map((tile) => {
        const row = parsePositiveInteger(tile.dataset.editorialTileRow, 2);
        const mappedColumn = mapEditorialColumn(tile.dataset.editorialTileColumn, activeColumns);

        return {
          tile,
          targetSlot: (row - 1) * activeColumns + mappedColumn,
        };
      })
      .sort((a, b) => a.targetSlot - b.targetSlot);

    tileData.forEach((entry) => {
      const productSlotsBeforeTile = entry.targetSlot - 1 - visibleTiles.length;
      const canRenderWithoutGap = productSlotsBeforeTile <= productItems.length;

      entry.tile.hidden = !canRenderWithoutGap;

      if (canRenderWithoutGap) {
        const referenceNode = productItems[productSlotsBeforeTile] || null;

        if (!isTileInPosition(grid, entry.tile, referenceNode)) {
          grid.insertBefore(entry.tile, referenceNode);
        }

        visibleTiles.push(entry);
      }
    });

    root.dataset.editorialTilesReady = 'true';
  };

  const revealGridItems = (root) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const items = Array.from(root.querySelectorAll('#product-grid .grid__item.scroll-trigger.animate--slide-in'));

    if (!items.length) {
      return;
    }

    items.slice(0, 36).forEach((item, index) => {
      item.classList.remove('scroll-trigger--cancel');
      item.classList.add('scroll-trigger--offscreen');
      item.style.setProperty('--animation-order', index + 1);
      item.style.animation = 'none';
    });

    root.offsetHeight;

    window.requestAnimationFrame(() => {
      items.slice(0, 36).forEach((item) => {
        item.style.animation = '';
        item.classList.remove('scroll-trigger--offscreen');
      });
    });
  };

  const applyView = (root, requestedView = getStoredView(), options = {}) => {
    const viewport = isDesktop() ? 'desktop' : 'mobile';
    const view = normalizeView(requestedView);
    const activeColumns = getActiveColumnCount(root, viewport, view);

    root.classList.remove(...viewClasses);
    root.classList.add(`collection-grid-view--${viewport}-${view}`);
    root.dataset.collectionGridViewCurrent = view;

    root.querySelectorAll('[data-collection-grid-view-button]').forEach((button) => {
      const buttonView = button.dataset.gridView;
      const isViewportButton =
        viewport === 'desktop' ? buttonView === '4' || buttonView === '6' : buttonView === '2' || buttonView === '3';

      button.setAttribute('aria-pressed', String(isViewportButton && buttonView === view));
    });

    if (options.layoutEditorialTiles !== false) {
      layoutEditorialTiles(root, activeColumns);
    }

    if (options.reveal) {
      revealGridItems(root);
    }
  };

  const observeProductGrid = (root) => {
    const grid = root.querySelector('#product-grid');

    if (!grid || grid.dataset.collectionGridViewObserved === 'true') {
      return;
    }

    grid.dataset.collectionGridViewObserved = 'true';

    new MutationObserver((mutations) => {
      const hasAddedProductItems = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) {
            return false;
          }

          return (
            node.matches('[data-collection-product-grid-item]') ||
            Boolean(node.querySelector('[data-collection-product-grid-item]'))
          );
        })
      );

      if (hasAddedProductItems) {
        window.requestAnimationFrame(() => applyView(root));
      }
    }).observe(grid, { childList: true });
  };

  const observeGrid = (root) => {
    const gridContainer = root.querySelector('#ProductGridContainer');

    if (!gridContainer || gridContainer.dataset.collectionGridViewObserved === 'true') {
      return;
    }

    gridContainer.dataset.collectionGridViewObserved = 'true';

    new MutationObserver(() => {
      applyView(root);
      observeProductGrid(root);
    }).observe(gridContainer, { childList: true, subtree: false });
  };

  const initRoot = (root) => {
    const initialView = getStoredView();
    const shouldLayoutInitialTiles = initialView === '6' || initialView === '3';

    applyView(root, initialView, { layoutEditorialTiles: shouldLayoutInitialTiles });
    observeGrid(root);
    observeProductGrid(root);
  };

  const initAll = () => {
    document.querySelectorAll('[data-collection-grid-view]').forEach(initRoot);
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-collection-grid-view-button]');

    if (!button) {
      return;
    }

    const root = button.closest('[data-collection-grid-view]');

    if (!root) {
      return;
    }

    event.preventDefault();

    const view = button.dataset.gridView;

    setStoredView(view);
    applyView(root, view, { reveal: true });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);

  if (desktopQuery.addEventListener) {
    desktopQuery.addEventListener('change', initAll);
  } else {
    desktopQuery.addListener(initAll);
  }
})();
